/**
 * BulkBatchRecordsDrawer Component
 *
 * Right-side drawer displaying batch details and individual records.
 * Uses Equal DS components for consistent styling.
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerOverlay,
  Table,
  type TableColumn,
  SearchBar,
  Tag,
  Button,
  Loader,
} from 'equal-ds-ui';
import {
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  X,
  ExternalLink,
  Download,
} from 'lucide-react';
import { Select } from '../../ui/Select';
import { Toast } from '../../ui/Toast';
import { ReportDownloadDropdown } from '../../ui/ReportDownloadDropdown';
import { CopyButton } from '../../ui/CopyButton';
import { bulkMisAPI } from '../../../services/bulk-mis-api.service';
import { downloadAPI } from '../../../services/download-api.service';
import { envService } from '../../../services/env.service';
import { isBulkOrchestrationV2Enabled } from '../../../utils/feature-flags';
import type {
  BulkBatchRecord,
  BulkIndividualRecord,
  BulkMisPagination,
  BulkBatchDetailsSummary,
} from '../../../types/bulk-mis';

interface BulkBatchRecordsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onExportError?: (message: string) => void;
  batch: BulkBatchRecord | null;
  records: BulkIndividualRecord[];
  pagination: BulkMisPagination;
  loading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onStatusFilterChange: (status: string) => void;
  onSearchChange: (search: string) => void;
  // Optional props for detailed batch info from API
  summary?: BulkBatchDetailsSummary;
  // Optional: Hide the Records/Records with Balance tab switcher (default: show based on config)
  showTabSwitcher?: boolean;
  /** Controls V2-specific UI. Only 'data_fetch' gets V2 gating. */
  screenName?: string;
}

export const BulkBatchRecordsDrawer: React.FC<BulkBatchRecordsDrawerProps> = ({
  isOpen,
  onClose,
  onExportError,
  batch,
  records,
  pagination,
  loading,
  onPageChange,
  onPageSizeChange,
  onStatusFilterChange,
  onSearchChange,
  summary,
  showTabSwitcher,
  screenName,
}) => {
  const isV2 = screenName === 'data_fetch' && isBulkOrchestrationV2Enabled();
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState<'records' | 'balances'>('records');
  const [searchValue, setSearchValue] = useState('');
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const batchInProgress = batch?.status === 'UPLOADED' || batch?.status === 'PROCESSING' || batch?.status === 'IN_PROGRESS' || batch?.status === 'READY';

  // Download button visibility from DB config (userinfo.ui_config.common) with envService fallback
  const showDownloadButton = useMemo(() => {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    const userInfo = encoded ? JSON.parse(atob(encoded)) : {};
    return userInfo?.ui_config?.common?.show_download_button_bulk_fetch ?? envService.getUIConfig().common.show_download_button_bulk_fetch;
  }, []);

  // Toast handlers
  const showToast = useCallback((message: string, variant: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const closeToast = useCallback(() => {
    setToastMessage(null);
  }, []);
  const totalItems = pagination.total_records ?? records.length;
  const totalPages =
    pagination.total_pages && pagination.total_pages > 0
      ? pagination.total_pages
      : Math.max(1, Math.ceil(totalItems / Math.max(pagination.limit || 1, 1)));

  const shouldShowTabs = (): boolean => {
    // If showTabSwitcher prop is explicitly false, hide tabs
    if (showTabSwitcher === false) {
      return false;
    }

    try {
      // V2 mode: show tabs based on batch_type (balance = fi-request workflow)
      const isV2 = isBulkOrchestrationV2Enabled();
      if (isV2) {
        return batch?.batch_type === 'balance';
      }

      // V1 mode: use isBalanceDataIncluded config
      const configStr = sessionStorage.getItem('isBalanceDataIncluded');
      if (configStr !== null) {
        return configStr === 'true';
      }
    } catch (error) {
      console.error('[BulkBatchRecordsDrawer] Error reading tabs config from session storage:', error);
    }

    return true;
  };

  // Check if any records have account_details (V2 responses won't have them)
  const hasAccountDetails = useMemo(() => {
    return records.some(record => record.account_details && record.account_details.length > 0);
  }, [records]);

  const showTabs = shouldShowTabs() && hasAccountDetails;

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Get record status badge
  const getRecordStatusBadge = (status: BulkIndividualRecord['status']) => {
    type StatusConfig = {
      label: string;
      className: string;
      icon: typeof CheckCircle2;
      displayLines?: string[];
    };

    const statusConfig: Record<string, StatusConfig> = {
      COMPLETED: { label: 'Completed', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800', icon: XCircle },
      IN_PROGRESS: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      PENDING_FROM_AA: {
        label: 'Pending from AA',
        className: 'bg-yellow-100 text-yellow-800',
        icon: Clock,
        displayLines: ['Pending', 'from AA'],
      },
      READY: { label: 'Ready', className: 'bg-blue-100 text-blue-800', icon: Clock },
    };

    const config = statusConfig[status] || statusConfig.READY;
    const Icon = config.icon;
    const label = config.displayLines ? (
      <span className="flex flex-col leading-tight">
        {config.displayLines.map((line: string, index: number) => (
          <span key={index}>{line}</span>
        ))}
      </span>
    ) : (
      config.label
    );

    return (
      <Tag className={`${config.className} inline-flex items-start gap-1`} size="sm">
        <Icon className="w-3 h-3 shrink-0 mt-0.5" />
        {typeof label === 'string' ? <span className="break-words">{label}</span> : label}
      </Tag>
    );
  };

  // Define columns for records table
  const columns: TableColumn<BulkIndividualRecord>[] = useMemo(
    () => [
      {
        key: 'consent_id',
        header: 'Consent ID',
        width: 220,
        accessor: (row: BulkIndividualRecord) => row.consent_id,
        render: (_value: string, row: BulkIndividualRecord) => (
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span
                className="font-mono text-sm text-gray-900 cursor-pointer hover:text-blue-600 truncate"
                title={row.consent_id}
              >
                {row.consent_id.length > 24 ? `${row.consent_id.substring(0, 24)}...` : row.consent_id}
              </span>
              <CopyButton text={row.consent_id} />
            </div>
            {row.fip_ids && row.fip_ids.length > 0 && (
              <span className="text-xs text-gray-700 mt-0.5">
                FIP: {row.fip_ids.join(', ')}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: 130,
        wrap: true,
        accessor: (row: BulkIndividualRecord) => row.status,
        render: (_value: string, row: BulkIndividualRecord) => getRecordStatusBadge(row.status),
      },
      {
        key: 'error',
        header: 'Error Details',
        width: 300,
        accessor: (row: BulkIndividualRecord) => row.error?.message || '-',
        render: (_value: string, row: BulkIndividualRecord) => (
          <div className="text-sm">
            {row.error ? (
              <div className="space-y-1">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded">
                        {row.error.code}
                      </span>
                      {row.error.http_status && (
                        <span className="text-xs text-gray-700">HTTP {row.error.http_status}</span>
                      )}
                    </div>
                    <p className="text-xs text-red-600 line-clamp-2">{row.error.message}</p>
                    {row.error.retry_guidance && (
                      <p className="text-xs text-gray-700 mt-1 italic line-clamp-1">
                        {row.error.retry_guidance}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        ),
      },
      {
        key: 'timing',
        header: 'Timing',
        width: 160,
        accessor: (row: BulkIndividualRecord) => row.timing?.completed_at || row.updated_at,
        render: (_value: string, row: BulkIndividualRecord) => (
          <div className="text-xs space-y-0.5">
            {row.timing ? (
              <>
                <div className="text-gray-800">
                  <span className="text-gray-600">Started:</span> {formatDate(row.timing.started_at)}
                </div>
                {row.timing.duration_ms > 0 && (
                  <div className="text-gray-700">
                    Duration: {row.timing.duration_ms}ms
                  </div>
                )}
              </>
            ) : (
              <div className="text-gray-800">{formatDate(row.updated_at)}</div>
            )}
          </div>
        ),
      },
    ],
    []
  );

  type BalanceRow = {
    id: string;
    session_id: string | null;
    consent_id: string;
    status: BulkIndividualRecord['status'];
    masked_account_number: string;
    balance_amount?: string;
    balance_currency?: string;
    link_reference_number: string;
    fi_status?: string;
    has_balance: boolean;
  };

  const balanceRows = useMemo<BalanceRow[]>(() => {
    const rows: BalanceRow[] = [];

    records.forEach((record) => {
      const accounts = record.account_details || [];
      accounts.forEach((account, index) => {
        const balanceAmount = account?.balance?.amount;
        const hasBalance = balanceAmount !== undefined && balanceAmount !== null && balanceAmount !== '';
        const shouldInclude = hasBalance || (record.status === 'FAILED' && !hasBalance);

        if (!shouldInclude) {
          return;
        }

        rows.push({
          id: `${record.id}-${account.link_reference_number || index}`,
          session_id: record.session_id,
          consent_id: record.consent_id,
          status: record.status,
          masked_account_number: account.masked_account_number || '-',
          balance_amount: account.balance?.amount,
          balance_currency: account.balance?.currency,
          link_reference_number: account.link_reference_number || '',
          fi_status: account.fi_status || '',
          has_balance: hasBalance
        });
      });
    });

    return rows;
  }, [records]);

  const balanceColumns: TableColumn<BalanceRow>[] = useMemo(
    () => [
      {
        key: 'session_id',
        header: 'Session ID',
        width: 220,
        accessor: (row: BalanceRow) => row.session_id || '-',
        render: (_value: string, row: BalanceRow) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-gray-900 truncate" title={row.session_id || 'N/A'}>
              {row.session_id || 'N/A'}
            </span>
            {row.session_id && (
              <CopyButton text={row.session_id} />
            )}
          </div>
        ),
      },
      {
        key: 'consent_id',
        header: 'Consent ID',
        width: 220,
        accessor: (row: BalanceRow) => row.consent_id,
        render: (_value: string, row: BalanceRow) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-gray-900 truncate" title={row.consent_id}>
              {row.consent_id.length > 24 ? `${row.consent_id.substring(0, 24)}...` : row.consent_id}
            </span>
            <CopyButton text={row.consent_id} />
          </div>
        ),
      },
      {
        key: 'masked_account_number',
        header: 'Masked Account Number',
        width: 200,
        accessor: (row: BalanceRow) => row.masked_account_number,
        render: (_value: string, row: BalanceRow) => (
          <span className="font-mono text-sm text-gray-900">
            {row.masked_account_number || '-'}
          </span>
        ),
      },
      {
        key: 'balance',
        header: 'Balance',
        width: 140,
        accessor: (row: BalanceRow) => row.balance_amount || '',
        render: (_value: string, row: BalanceRow) => {
          if (!row.has_balance) {
            return <span className="text-sm text-gray-400">-</span>;
          }
          const currency = row.balance_currency ? ` ${row.balance_currency}` : '';
          return <span className="text-sm text-gray-900">{row.balance_amount}{currency}</span>;
        },
      },
      {
        key: 'fi_status',
        header: 'FI Status',
        width: 140,
        accessor: (row: BalanceRow) => row.fi_status || '',
        render: (_value: string, row: BalanceRow) => {
          if (row.status === 'FAILED' && !row.has_balance) {
            return <span className="text-sm text-gray-900">{row.fi_status || '-'}</span>;
          }
          return <span className="text-sm text-gray-400">-</span>;
        },
      },
    ],
    []
  );

  // Handle search
  const handleSearchSubmit = (value: string) => {
    setSearchValue(value);
    onSearchChange(value);
  };

  // Handle search input change - auto-search when cleared
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    // Auto-search when input is cleared
    if (value === '') {
      onSearchChange('');
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    onStatusFilterChange(value);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setStatusFilter('all');
    onStatusFilterChange('all');
    onSearchChange('');
    setSearchValue('');
  };


  // Check if any filters are active
  const hasActiveFilters = statusFilter !== 'all';

  // Calculate summary from batch or use provided summary
  const displaySummary = summary || {
    total: batch?.total_records || 0,
    ready: 0,
    in_progress: 0,
    completed: batch?.successful_records || 0,
    failed: batch?.failed_records || 0,
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-[60] max-w-md">
          <Toast message={toastMessage} onClose={closeToast} variant={toastVariant} />
        </div>
      )}
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="70%"
        className="border-border-default bg-gradient-to-b from-background-secondary to-primary-50"
      >
        <DrawerHeader
          title={batch?.reference_id || 'Batch Records'}
          subtitle=""
          icon={<FileText className="w-5 h-5" />}
          onClose={onClose}
        />

        {/* Type Tag, Tabs & View EWS */}
        <div className="px-6 pt-2 pb-0 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isV2 && batch?.batch_type === 'loan_monitoring' && (
              <Tag className="bg-blue-100 text-blue-800 whitespace-nowrap" size="sm">Loan Monitoring</Tag>
            )}
            {isV2 && batch?.batch_type === 'balance' && (
              <Tag className="bg-purple-100 text-purple-800" size="sm">Balance</Tag>
            )}
            {isV2 && !batch?.batch_type && (
              <Tag className="bg-gray-100 text-gray-600" size="sm">N/A</Tag>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showTabs && (
              <>
                <Button
                  onClick={() => setActiveTab('records')}
                  variant={activeTab === 'records' ? 'primary' : 'secondary'}
                  size="sm"
                  className="inline-flex items-center gap-1"
                >
                  Records
                </Button>
                <Button
                  onClick={() => setActiveTab('balances')}
                  variant={activeTab === 'balances' ? 'primary' : 'secondary'}
                  size="sm"
                  className="inline-flex items-center gap-1"
                >
                  Records with Balance
                </Button>
              </>
            )}
            {isV2 && (batch?.ews_data || batch?.ews_enabled || batch?.batch_type === 'loan_monitoring') && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  const ewsRoute = `/dashboard/insights/ews?batch_id=${batch.batch_id}&reference_id=${encodeURIComponent(batch.reference_id)}`;
                  window.location.hash = `#${ewsRoute}`;
                  window.dispatchEvent(new CustomEvent('angular-route-change', {
                    detail: { currentRoute: ewsRoute }
                  }));
                }}
                disabled={batchInProgress}
                className="inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View EWS
              </Button>
            )}
          </div>
        </div>

        <DrawerContent className="equal-ds-root flex flex-col flex-1 overflow-hidden p-6 space-y-6">
          <div ref={drawerContentRef} className="flex flex-col flex-1 min-h-0 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-black">Total Records</span>
                <FileText className="w-5 h-5 text-gray-400" />
              </div>
              <div className="text-3xl font-bold text-gray-900">{displaySummary.total}</div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-black">Successful Fetches</span>
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-green-600">
                {displaySummary.completed}
                {displaySummary.total > 0 && (
                  <span className="text-lg font-medium text-green-400 ml-2">
                    ({Math.round((displaySummary.completed / displaySummary.total) * 100)}%)
                  </span>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-black">Failed Fetches</span>
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div className="text-3xl font-bold text-red-600">
                {displaySummary.failed}
                {displaySummary.total > 0 && (
                  <span className="text-lg font-medium text-red-400 ml-2">
                    ({Math.round((displaySummary.failed / displaySummary.total) * 100)}%)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex-shrink-0">
            <div className="flex flex-row gap-4 items-center">
              <div className="flex-1">
                <SearchBar
                  placeholder="Search by consent ID..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onSearch={handleSearchSubmit}
                  className="w-full"
                />
              </div>
              <div className="w-48">
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'IN_PROGRESS', label: 'In Progress' },
                    { value: 'COMPLETED', label: 'Completed' },
                    { value: 'FAILED', label: 'Failed' },
                  ]}
                />
              </div>
              {hasActiveFilters && (
                <Button
                  onClick={handleResetFilters}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Reset
                </Button>
              )}
              {showDownloadButton ? (
                <ReportDownloadDropdown
                  buttonLabel="Download Data"
                  buttonVariant="primary"
                  reportTypes={[
                    { value: 'ews', title: 'EWS Report', description: 'Single report with balance information for all records' },
                    { value: 'raw', title: 'Raw Data', description: 'Single report with balance information for all records' },
                  ]}
                  defaultReportType="ews"
                  formats={[{ value: 'csv', label: '.csv' }]}
                  defaultFormat="csv"
                  onGenerate={async ({ reportType, format }) => {
                    const isJobBasedDownloadEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';
                    if (isJobBasedDownloadEnabled) {
                      const downloadType = reportType === 'ews' ? 'EWS_REPORT' : 'FI_RAW_DATA';
                      await downloadAPI.createBatchJob(batch!.batch_id, downloadType);
                      showToast('Download job created. Your file will be ready shortly in the Download Center.', 'success');
                      return null;
                    }

                    return bulkMisAPI.exportBatchRecords({
                      reference_id: batch!.reference_id,
                      status: statusFilter !== 'all' ? statusFilter : undefined,
                      search: searchValue || undefined,
                      include_balances: true,
                      report_type: reportType,
                      format,
                    });
                  }}
                  getFileName={({ reportType, format }) => {
                    const label = reportType === 'ews' ? 'ews-report' : 'raw-data';
                    return `${label}-${batch?.reference_id || 'export'}.${format}`;
                  }}
                  postGenerateAction={{
                    label: 'GO TO DOWNLOAD CENTER',
                    onClick: () => {
                      if ((window as any).AngularAuthBridge?.navigate) {
                        (window as any).AngularAuthBridge.navigate('/dashboard/download-centre');
                      } else {
                        window.location.href = '/dashboard/download-centre';
                      }
                    },
                  }}
                  onError={() => {
                    if (onExportError) {
                      onExportError('Failed to export records. Please try again.');
                    } else {
                      showToast('Failed to export records. Please try again.', 'error');
                    }
                  }}
                  disabled={!batch?.reference_id || batchInProgress}
                  panelContainerRef={drawerContentRef}
                />
              ) : (
                <Button
                  variant="primary"
                  disabled={!batch?.reference_id || batchInProgress}
                  className="flex items-center gap-2"
                  onClick={async () => {
                    try {
                      const blob = await bulkMisAPI.exportBatchRecords({
                        reference_id: batch!.reference_id,
                        status: statusFilter !== 'all' ? statusFilter : undefined,
                        search: searchValue || undefined,
                        include_balances: true,
                        report_type: 'raw',
                        format: 'csv',
                      });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `raw-data-${batch?.reference_id || 'export'}.csv`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    } catch {
                      if (onExportError) {
                        onExportError('Failed to export records. Please try again.');
                      } else {
                        showToast('Failed to export records. Please try again.', 'error');
                      }
                    }
                  }}
                >
                  <Download className="w-4 h-4" />
                  Export Records
                </Button>
              )}
            </div>
          </div>

          {/* Records Table */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {loading && records.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 shadow-sm">
                <div className="flex flex-col items-center justify-center gap-4">
                  <Loader cellSize={12} />
                  <p className="text-gray-600">Loading records...</p>
                </div>
              </div>
            ) : activeTab === 'balances' && balanceRows.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 shadow-sm">
                <div className="text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">No balance records found</p>
                  <p className="text-sm text-gray-600 mt-1">Try adjusting your filters</p>
                </div>
              </div>
            ) : records.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 shadow-sm">
                <div className="text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">No records found</p>
                  <p className="text-sm text-gray-600 mt-1">Try adjusting your filters</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {activeTab === 'balances' ? 'Records with Balance' : 'Records'}
                    </h3>
                    <span className="text-sm text-black">
                      {activeTab === 'balances'
                        ? `Showing ${balanceRows.length} balance rows on this page`
                        : `Showing ${records.length} of ${totalItems} records`}
                    </span>
                  </div>
                </div>
                <div className="overflow-auto flex-1 min-h-0">
                  {activeTab === 'balances' ? (
                    <Table
                      data={balanceRows}
                      columns={balanceColumns}
                      loading={loading}
                      pagination={{
                        currentPage: pagination.page || 1,
                        pageSize: pagination.limit || 25,
                        totalItems,
                        totalPages,
                        onPageChange,
                        onPageSizeChange,
                        pageSizeOptions: [10, 25, 50, 100],
                        dropdownZIndex: 1500,
                      }}
                    />
                  ) : (
                    <Table
                      data={records}
                      columns={columns}
                      loading={loading}
                      pagination={{
                        currentPage: pagination.page || 1,
                        pageSize: pagination.limit || 25,
                        totalItems,
                        totalPages,
                        onPageChange,
                        onPageSizeChange,
                        pageSizeOptions: [10, 25, 50, 100],
                        dropdownZIndex: 1500,
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
